import React from 'react';
import {
  DropDownList,
  DropDownListChangeEvent
} from '@progress/kendo-react-dropdowns';
import cloneDeep from 'lodash.clonedeep';
import { useParams } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import {
  RadioButton,
  RadioGroup,
  RadioGroupChangeEvent
} from '@progress/kendo-react-inputs';
import { Window, WindowActionsBar } from '@progress/kendo-react-dialogs';

import {
  ERR_INPUT_REQUIRED,
  ERR_DUPLICATE_NAME_VALUE
} from '../../../constants/text';
import {
  GetResourceEntity,
  GetResourceValue,
  UpdateGameDocState,
  SaveTimerEntityEditorAsync
} from '../../../utils/game-document';
import {
  TimerEntity,
  ResourceEntity
} from '../../../types/game-document/entities';
import { uuid } from '../../../types/common-helper';
import RequiredFields from '../../../types/required-fields';
import { TimerEditorFull } from '../timers/timer-editor-full';
import { GameDocumentContext } from '../../../contexts/game-document';
import { ResourceType } from '../../../types/game-document/resource-type';
import { EntityEditor } from '../../../types/game-document/entity-editor';

const titleGuid = uuid();

const defaultNewEntity: TimerEntity = {
  id: uuid(),
  name: '',
  description: '',
  titleResId: titleGuid,
  direction: 'down',
  repeats: false
};

const defaultTitleResource: ResourceEntity = {
  id: titleGuid,
  name: 'Title',
  description: '',
  type: 'text',
  value: ''
};

export const TimerForm: React.FC<{ timerId?: string }> = ({ timerId }) => {
  const [state, setState] = React.useContext(GameDocumentContext);
  const [entity, setEntity] = React.useState<TimerEntity>(defaultNewEntity);
  const [resources, setResources] = React.useState<ResourceEntity[]>([
    defaultTitleResource
  ]);

  const [requiredFields, setRequiredFields] = React.useState<
    RequiredFields<TimerEntity>[]
  >([
    { name: 'name', errorMessage: '' },
    { name: 'titleResId', errorMessage: '' }
  ]);

  const updateEntityAsync = async (
    name: keyof TimerEntity,
    value: string | number,
    type: ResourceType,
    isClearValue?: boolean
  ) => {
    if (timerId) {
      setRequiredFields((prev) =>
        prev.map((task: RequiredFields<TimerEntity>) => {
          const copyTask = cloneDeep({ ...task });
          if (task.name === name) {
            copyTask.errorMessage = '';
            if (name === 'name') {
              const duplicateValueIndex: number =
                state.gameDocument?.assets?.timers
                  ?.filter((item: TimerEntity) => item.id !== entity.id)
                  .findIndex(
                    (item: TimerEntity) =>
                      item.name.toLowerCase() ===
                      (value as string)?.toLowerCase()
                  ) ?? -1;
              if (value === '') {
                copyTask.errorMessage = ERR_INPUT_REQUIRED;
              } else if (duplicateValueIndex !== -1) {
                copyTask.errorMessage = ERR_DUPLICATE_NAME_VALUE;
              }
            } else if (name === 'titleResId') {
              if (value === '') {
                copyTask.errorMessage = ERR_INPUT_REQUIRED;
              }
            }
          }
          return copyTask;
        })
      );

      let updatedEntity: TimerEntity = cloneDeep({ ...entity });
      let updatedResources: ResourceEntity[] = [...resources];

      if (!name?.toLowerCase().includes('resid')) {
        updatedEntity[name] = value as never;
      } else {
        const resourceId = entity[name];
        const resIndex: number = updatedResources?.findIndex(
          (x) => x.id === resourceId
        );
        if (resIndex === -1) {
          const newId: string = uuid();
          const resourceEntity: ResourceEntity = {
            id: newId,
            description: '',
            name: name,
            type: type,
            value: value as string
          };
          updatedResources.push(resourceEntity);
          updatedEntity[name] = newId as never;
        } else {
          updatedResources[resIndex].value = value as never;
          updatedEntity[name] = resourceId as never;
        }
      }

      submitEntityAsync(updatedEntity, updatedResources);
    } else {
      if (!name?.toLowerCase().includes('resid')) {
        setEntity((prev) => ({ ...prev, [name]: isClearValue ? '' : value }));
      } else {
        const resourceId = entity[name];
        let updatedResources: ResourceEntity[] = [...resources];
        if (resourceId === undefined || resourceId === '') {
          const newId: string = uuid();
          const resourceEntity: ResourceEntity = {
            id: newId,
            description: '',
            name: name,
            type: type,
            value: value as string
          };
          updatedResources.push(resourceEntity);
          setResources(updatedResources);
          setEntity((prev) => ({ ...prev, [name]: newId }));
        } else {
          setResources(
            updatedResources.map((res) =>
              res?.id === resourceId
                ? { ...res, value: isClearValue ? '' : (value as string) }
                : { ...res }
            )
          );
          setEntity((prev) => ({ ...prev, [name]: resourceId }));
        }
      }
    }
  };

  const submitEntityAsync = async (
    updatedEntity: TimerEntity,
    updatedResources: ResourceEntity[]
  ) => {
    const entityResources = updatedResources?.map((item) => ({
      isNew: false,
      entity: item
    })) as EntityEditor<ResourceEntity>[];

    setEntity(updatedEntity);
    setResources(updatedResources);

    const response = await SaveTimerEntityEditorAsync(
      state?.gameDocument!,
      { isNew: false, entity: updatedEntity },
      entityResources,
      false
    );
    setState((prev) => UpdateGameDocState(prev, response));
  };

  React.useEffect(() => {
    if (!timerId) return;
    const timer = state.gameDocument?.assets.timers?.find(
      (i) => i.id === timerId
    );
    if (timer) {
      setEntity(timer);
      setResources([
        GetResourceEntity(state?.gameDocument!, timer.titleResId ?? '')
      ]);
    }
  }, [timerId]);

  return (
    <TimerEditorFull
      key={entity.id}
      timer={entity}
      resources={resources}
      requiredFields={requiredFields}
      handleEntityChange={(name, value, type, isClearValue) => {
        updateEntityAsync(name, value, type, isClearValue);
      }}
      handleEventAction={(entity) => {
        timerId ? submitEntityAsync(entity, resources) : setEntity(entity);
      }}
    />
  );
};

export const MapTimerWindow: React.FC<{
  selectedMenu: 'Manage' | 'Replace';
  initialTimerId?: string;
  setActionEditTimerDialogVisible: (
    value: React.SetStateAction<boolean>
  ) => void;
}> = ({ selectedMenu, initialTimerId, setActionEditTimerDialogVisible }) => {
  const { zoneId } = useParams();
  const [state, setState] = React.useContext(GameDocumentContext);

  const [selectedTimerId, setSelectedTimerId] = React.useState<string | null>(
    initialTimerId ?? null
  );

  const [timerOption, setTimerOption] = React.useState<'current' | 'new'>(
    'current'
  );

  const initNewTimer = () => {
    const titleResId = uuid();
    const entity: TimerEntity = {
      id: uuid(),
      name: '',
      description: '',
      titleResId,
      direction: 'down',
      repeats: false
    };
    const resources: ResourceEntity[] = [
      {
        id: titleResId,
        name: 'Title',
        description: '',
        type: 'text',
        value: ''
      }
    ];
    return { entity, resources };
  };

  const [{ entity: newEntity, resources: newResources }, setNewTimerState] =
    React.useState(initNewTimer);

  const setNewEntity = (entity: TimerEntity) =>
    setNewTimerState((prev) => ({ ...prev, entity }));

  const [newRequiredFields, setNewRequiredFields] = React.useState<
    RequiredFields<TimerEntity>[]
  >([{ name: 'titleResId', errorMessage: '' }]);

  React.useEffect(() => {
    if (initialTimerId) {
      setSelectedTimerId(initialTimerId);
    }
  }, [initialTimerId]);

  const timer = React.useMemo(() => {
    const { gameDocument } = state;

    if (selectedTimerId !== null)
      return (gameDocument?.assets.timers ?? []).find(
        (t) => t.id === selectedTimerId
      );

    let id = '';
    if (zoneId) {
      const zoneIndex = gameDocument?.rules?.worldMap?.zones?.findIndex(
        (x) => x?.zoneAssId === zoneId
      );
      if (zoneIndex !== undefined && zoneIndex !== -1) {
        id = gameDocument?.rules.worldMap.zones[zoneIndex].timerAssId ?? '';
      }
    } else {
      id = gameDocument?.rules.worldMap?.timerAssId ?? '';
    }

    return (gameDocument?.assets.timers ?? []).find((t) => t.id === id);
  }, [zoneId, state.gameDocument, selectedTimerId]);

  const existingTimerOptions = React.useMemo(() => {
    const list = (state.gameDocument?.assets.timers ?? []).map((t) => {
      const title =
        GetResourceValue(state.gameDocument!, t.titleResId ?? '') ||
        t.name ||
        'Untitled timer';
      return {
        id: t.id,
        name: title
      };
    });
    return [{ id: '', name: '(No timer)' }, ...list];
  }, [state.gameDocument]);

  const currentDropdownValue = React.useMemo(() => {
    const currentId =
      selectedTimerId !== null ? selectedTimerId : (timer?.id ?? '');
    return (
      existingTimerOptions.find((opt) => opt.id === currentId) ??
      existingTimerOptions[0]
    );
  }, [existingTimerOptions, selectedTimerId, timer]);

  const handleNewEntityChange = (
    name: keyof TimerEntity,
    value: string | number,
    type: ResourceType,
    isClearValue?: boolean
  ) => {
    setNewRequiredFields((prev) =>
      prev.map((field) => {
        if (field.name !== name) return field;
        const copy = cloneDeep(field);
        copy.errorMessage = '';
        if (name === 'titleResId' && (isClearValue || value === ''))
          copy.errorMessage = ERR_INPUT_REQUIRED;
        return copy;
      })
    );

    setNewTimerState((prev) => {
      if (!name?.toLowerCase().includes('resid')) {
        return {
          ...prev,
          entity: { ...prev.entity, [name]: isClearValue ? '' : value }
        };
      }

      const resourceId = prev.entity[name] as string | undefined;
      const updatedResources = [...prev.resources];
      const strVal = isClearValue ? '' : (value as string);

      if (!resourceId) {
        const newId = uuid();
        updatedResources.push({
          id: newId,
          description: '',
          name,
          type,
          value: strVal
        });
        return {
          entity: {
            ...prev.entity,
            [name]: newId,
            ...(name === 'titleResId' ? { name: strVal } : {})
          },
          resources: updatedResources
        };
      } else {
        return {
          entity: {
            ...prev.entity,
            [name]: resourceId,
            ...(name === 'titleResId' ? { name: strVal } : {})
          },
          resources: updatedResources.map((r) =>
            r.id === resourceId ? { ...r, value: strVal } : r
          )
        };
      }
    });
  };

  const isNewEntityValid = (): boolean => {
    const titleResource = newResources.find(
      (r) => r.id === newEntity.titleResId
    );
    const hasTitle = !!titleResource?.value?.trim();

    setNewRequiredFields((prev) =>
      prev.map((field) => {
        const copy = cloneDeep(field);
        if (field.name === 'titleResId') {
          copy.errorMessage = hasTitle ? '' : ERR_INPUT_REQUIRED;
        }
        return copy;
      })
    );

    return hasTitle;
  };

  const onChangeEditTimerHandler = (
    value: string,
    gameDocument: (typeof state)['gameDocument']
  ) => {
    if (!gameDocument) return;

    const docCopy = cloneDeep(gameDocument);
    if (zoneId) {
      const zoneIndex = docCopy.rules?.worldMap?.zones?.findIndex(
        (x) => x?.zoneAssId === zoneId
      );
      if (zoneIndex !== undefined && zoneIndex !== -1) {
        docCopy.rules.worldMap.zones[zoneIndex].timerAssId = value;
      }
    } else {
      if (!docCopy.rules.worldMap) {
        docCopy.rules.worldMap = {} as any;
      }
      docCopy.rules.worldMap.timerAssId = value;
    }

    setState((prev) => UpdateGameDocState(prev, docCopy));
  };

  const handleConfirm = async () => {
    if (timerOption === 'new') {
      if (!isNewEntityValid()) return;

      const titleVal =
        newResources.find((r) => r.id === newEntity.titleResId)?.value?.trim() ||
        'Timer';
      const entityToSave: TimerEntity = {
        ...newEntity,
        name: newEntity.name?.trim() ? newEntity.name : titleVal
      };

      const entityResources = newResources.map((item) => ({
        isNew: true,
        entity: item
      })) as EntityEditor<ResourceEntity>[];

      const response = await SaveTimerEntityEditorAsync(
        state.gameDocument!,
        { isNew: true, entity: entityToSave },
        entityResources
      );

      if (response) {
        const createdTimer = response.assets.timers?.find(
          (t) =>
            t.titleResId === entityToSave.titleResId ||
            t.name === entityToSave.name
        );
        const newTimerId = createdTimer?.id || entityToSave.id;
        onChangeEditTimerHandler(newTimerId, response);
      }
    } else {
      const idToSave =
        selectedTimerId !== null ? selectedTimerId : (timer?.id ?? '');
      onChangeEditTimerHandler(idToSave, state.gameDocument);
    }

    setActionEditTimerDialogVisible(false);
  };

  return (
    <Window
      modal={true}
      minimizeButton={undefined}
      className={'shadow'}
      initialWidth={650}
      initialHeight={550}
      onClose={() => setActionEditTimerDialogVisible(false)}
      title={'Map Timer'}>
      {(!initialTimerId || selectedMenu === 'Replace') && (
        <div style={{ marginBottom: '16px' }}>
          <RadioGroup
            data={[
              { label: 'Use existing timer', value: 'current' },
              { label: 'Create new', value: 'new' }
            ]}
            value={timerOption}
            onChange={(e: RadioGroupChangeEvent) => setTimerOption(e.value)}
            layout="horizontal"
            style={{ gap: 24 }}
          />
        </div>
      )}

      {timerOption === 'current' ? (
        <>
          {(!initialTimerId || selectedMenu === 'Replace') && (
            <DropDownList
              data={existingTimerOptions}
              value={currentDropdownValue}
              textField={'name'}
              dataItemKey={'id'}
              onChange={(event: DropDownListChangeEvent) => {
                setSelectedTimerId(event.value.id ?? '');
              }}
            />
          )}
          {currentDropdownValue.id !== '' && (
            <div
              style={{
                marginTop: '16px'
              }}>
              <hr />
              <TimerForm timerId={currentDropdownValue.id} />
            </div>
          )}
        </>
      ) : (
        <TimerEditorFull
          timer={newEntity}
          resources={newResources}
          requiredFields={newRequiredFields}
          handleEntityChange={handleNewEntityChange}
          handleEventAction={(entity) => setNewEntity(entity)}
        />
      )}

      <WindowActionsBar>
        <Button
          themeColor={'secondary'}
          onClick={() => setActionEditTimerDialogVisible(false)}>
          Cancel
        </Button>
        <Button themeColor={'primary'} onClick={handleConfirm}>
          {timerOption === 'current' ? 'Add timer' : 'Create timer'}
        </Button>
      </WindowActionsBar>
    </Window>
  );
};
